import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './layout/Layout.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import PageTransition from './components/PageTransition.jsx';
import CourseDashboardPage from './pages/CourseDashboardPage.jsx';
import CourseRequestPage from './pages/CourseRequestPage.jsx';
import CourseLearnPage from './pages/CourseLearnPage.jsx';
import InsightsPage from './pages/InsightsPage.jsx';
import HomeRedirect from './pages/HomeRedirect';
import SubscribePage from './pages/SubscribePage';
import SubscriptionCheckoutPage from './pages/SubscriptionCheckoutPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import SubscriptionCancelPage from './pages/SubscriptionCancelPage';
import Login from './auth/Login';
import RequireAuth from './auth/RequireAuth.jsx';
import RequireSubscription from './auth/RequireSubscription.jsx';
import Academy from './pages/Academy';
import PricingPage from './pages/PricingPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import HelpPage from './pages/HelpPage';
import Home from './pages/Home';
import ParentDashboardPage from './pages/ParentDashboardPage.jsx';
import StudentDetailShell from './pages/StudentDetailShell.jsx';
import StudentOverviewSection from './pages/StudentOverviewSection.jsx';
import StudentPortfolioSection from './pages/StudentPortfolioSection.jsx';
import StudentCourseDetailPage from './pages/StudentCourseDetailPage.jsx';
import StudentAssignCoursePage from './pages/StudentAssignCoursePage.jsx';
import SupervisorsSettingsPage from './pages/SupervisorsSettingsPage.jsx';
import SettingsPage from './pages/SettingsPage.jsx';
import InsightsPortfolioPage from './pages/InsightsPortfolioPage.jsx';
import GScorePage from './pages/GScorePage.jsx';
import GScoreTrainGamePage from './pages/GScoreTrainGamePage.jsx';
import GScoreAssessmentPage from './pages/GScoreAssessmentPage.jsx';
import CourseSharePreviewPage from './pages/CourseSharePreviewPage.jsx';
import PendingShareResumer from './features/courseShare/components/PendingShareResumer.jsx';
import ThemeSyncer from './theme/ThemeSyncer';
import { CelebrationProvider } from './features/celebration';
import { useAuth } from './auth/AuthProvider';

function ConditionalLayout({ children }) {
  const { pathname } = useLocation();
  // Strip trailing slash so prerendered routes match with or without one.
  // react-snap writes /academy/index.html, and Firebase serves that as
  // /academy/ (301 from the bare path).
  const normalized = pathname.replace(/\/+$/, '') || '/';
  // Standalone marketing pages render their own nav/footer and must not get
  // the app sidebar/topbar shell. `/` is the enterprise homepage. `/enterprise`
  // only exists long enough to redirect there.
  if (normalized === '/' || normalized === '/enterprise' || normalized === '/academy') return children;
  // Shared course preview is a focused standalone window — no sidebar/topbar.
  if (normalized.startsWith('/shared/')) return children;
  // The subscribe + checkout pages are one focused full-screen conversion
  // flow — an unsubscribed learner has no working nav (every destination just
  // gates back here), so the app shell is dead weight. Render them bare.
  if (normalized === '/subscribe' || normalized === '/subscription/checkout') return children;
  // Transient post-login resolver — self-contained full-screen loader that
  // immediately redirects, so the app shell would only flash.
  if (normalized === '/home') return children;
  return <Layout>{children}</Layout>;
}

// Logged-in visitors hitting the marketing root go to the app. Render the
// home page while auth is still resolving so react-snap (and share-card
// crawlers of the prerender) capture its meta tags instead of an empty shell.
function MarketingHome() {
  const { user, ready } = useAuth();
  if (ready && user) return <Navigate to="/dashboard" replace />;
  return <Home />;
}

export default function App() {

  return (
    <>
      <ThemeSyncer />
      <PendingShareResumer />
      <ErrorBoundary>
      <CelebrationProvider>
      <ConditionalLayout>
      <PageTransition>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<MarketingHome />} />
        <Route path="/academy" element={<Academy />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/enterprise" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<Login />} />
        {/* Shared course preview — public to VIEW; accepting requires auth + subscription (gated in-page) */}
        <Route path="/shared/:token" element={<CourseSharePreviewPage />} />

        {/* Protected + Subscription Required Routes */}
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <RequireSubscription>
                <CourseDashboardPage />
              </RequireSubscription>
            </RequireAuth>
          }
        />
        <Route
          path="/request"
          element={
            <RequireAuth>
              <RequireSubscription>
                <CourseRequestPage />
              </RequireSubscription>
            </RequireAuth>
          }
        />
        <Route
          path="/course/new"
          element={
            <RequireAuth>
              <RequireSubscription>
                <CourseRequestPage />
              </RequireSubscription>
            </RequireAuth>
          }
        />
        <Route
          path="/g-score"
          element={
            <RequireAuth>
              <RequireSubscription>
                <GScorePage />
              </RequireSubscription>
            </RequireAuth>
          }
        />
        <Route
          path="/g-score/train/:gameId"
          element={
            <RequireAuth>
              <RequireSubscription>
                <GScoreTrainGamePage />
              </RequireSubscription>
            </RequireAuth>
          }
        />
        <Route
          path="/g-score/assessment/:pillar"
          element={
            <RequireAuth>
              <RequireSubscription>
                <GScoreAssessmentPage />
              </RequireSubscription>
            </RequireAuth>
          }
        />
        <Route
          path="/insights"
          element={
            <RequireAuth>
              <RequireSubscription>
                <InsightsPage />
              </RequireSubscription>
            </RequireAuth>
          }
        />
        <Route
          path="/insights/portfolio"
          element={
            <RequireAuth>
              <RequireSubscription>
                <InsightsPortfolioPage />
              </RequireSubscription>
            </RequireAuth>
          }
        />

        {/* Learn views (protected + subscription required) */}
        <Route
          path="/courses/:courseId"
          element={
            <RequireAuth>
              <RequireSubscription>
                <CourseLearnPage />
              </RequireSubscription>
            </RequireAuth>
          }
        />
        <Route
          path="/courses/:courseId/sections/:sectionId"
          element={
            <RequireAuth>
              <RequireSubscription>
                <CourseLearnPage />
              </RequireSubscription>
            </RequireAuth>
          }
        />

        {/* Parent Portal - auth required, subscription NOT required (supervision is independent) */}
        <Route
          path="/family"
          element={
            <RequireAuth>
              <ParentDashboardPage />
            </RequireAuth>
          }
        />
        {/* Student detail: shell fetches once and keeps header + tabs
            mounted while inner tab content swaps via <Outlet />. */}
        <Route
          path="/family/students/:studentId"
          element={
            <RequireAuth>
              <StudentDetailShell />
            </RequireAuth>
          }
        >
          <Route index element={<StudentOverviewSection />} />
          <Route path="portfolio" element={<StudentPortfolioSection />} />
        </Route>
        {/* These use different layouts (no header/tabs), so they stay flat. */}
        <Route
          path="/family/students/:studentId/courses/:courseId"
          element={
            <RequireAuth>
              <StudentCourseDetailPage />
            </RequireAuth>
          }
        />
        <Route
          path="/family/students/:studentId/assign"
          element={
            <RequireAuth>
              <StudentAssignCoursePage />
            </RequireAuth>
          }
        />
        {/* Settings — auth required, subscription NOT required (account,
            preferences, and sharing must stay reachable by unsubscribed users). */}
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <SettingsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/sharing"
          element={
            <RequireAuth>
              <SupervisorsSettingsPage />
            </RequireAuth>
          }
        />

        {/* Post-login landing resolver — auth only; picks the right home from
            account state (see HomeRedirect). */}
        <Route
          path="/home"
          element={
            <RequireAuth>
              <HomeRedirect />
            </RequireAuth>
          }
        />

        {/* Subscription pages - auth required but NOT subscription required */}
        <Route
          path="/subscribe"
          element={
            <RequireAuth>
              <SubscribePage />
            </RequireAuth>
          }
        />
        <Route
          path="/subscription/checkout"
          element={
            <RequireAuth>
              <SubscriptionCheckoutPage />
            </RequireAuth>
          }
        />
        <Route
          path="/subscription/success"
          element={
            <RequireAuth>
              <SubscriptionSuccessPage />
            </RequireAuth>
          }
        />
        <Route
          path="/subscription/cancel"
          element={
            <RequireAuth>
              <SubscriptionCancelPage />
            </RequireAuth>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </PageTransition>
      </ConditionalLayout>
      </CelebrationProvider>
      </ErrorBoundary>
    </>
  );
}